import Button from "components/Button/Button";
import { KaboodleToaster } from "components/common";
import useQuickBooksConnectionStatus from "components/Invoicing/QuickBooks/useQuickBooksConnectionStatus";
import useQuickBooksReconnect from "components/Invoicing/QuickBooks/useQuickBooksReconnect";
import { Tooltip } from "components/Tooltip/Tooltip";
import { useEffect, useMemo, useState } from "react";
import {
  useBatchPostInvoicesMutation,
  useBatchVoidInvoicesMutation,
  useDeleteInvoiceByIdMutation,
  useLazyGetBatchSummaryQuery,
  useLazyGetBatchVoidSummaryQuery,
} from "services/invoice-api";

import { getRowId } from "../utils/reportUtils";

type BatchInvoiceActionsProps = {
  selectedRows: Set<string | number>;
  reportData: Record<string, unknown>[];
  onClearSelection: () => void;
  onUpdateSelection: (rows: Set<string | number>) => void;
  autoSelectEligible?: boolean;
};

/* batch invoice actions component, ie. batch post invoices, auto-select eligible invoices */
export const BatchInvoiceActions = ({
  selectedRows,
  reportData,
  onClearSelection,
  onUpdateSelection,
  autoSelectEligible = false,
}: BatchInvoiceActionsProps) => {
  const {
    isConnectionValid,
    isLoading: isConnectionLoading,
    connectionMessage,
  } = useQuickBooksConnectionStatus();
  const { startReconnection } = useQuickBooksReconnect();
  const [batchPostInvoices, { isLoading: isBatchPosting }] =
    useBatchPostInvoicesMutation();
  const [deleteInvoiceById, { isLoading: isDeleting }] =
    useDeleteInvoiceByIdMutation();
  const [batchVoidInvoices, { isLoading: isVoiding }] =
    useBatchVoidInvoicesMutation();
  const [fetchBatchSummary] = useLazyGetBatchSummaryQuery();
  const [fetchBatchVoidSummary] = useLazyGetBatchVoidSummaryQuery();
  const [showBulkConfirmation, setShowBulkConfirmation] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<
    "single" | "batch_prompt" | null
  >(null);
  const [showVoidConfirmation, setShowVoidConfirmation] = useState<
    "single" | "bulk_prompt" | "bulk_confirm" | null
  >(null);
  const [voidBatchSummary, setVoidBatchSummary] = useState<{
    invoiceCount: number;
    totalAmount: number;
    clientName: string | null;
    earliestDate: string | null;
    latestDate: string | null;
  } | null>(null);

  // auto-select eligible invoices when report data changes (if enabled)
  useEffect(() => {
    if (autoSelectEligible && reportData.length > 0) {
      const eligibleRowKeys = reportData
        .filter((row) => {
          const validForPosting = String(
            row["Valid for Posting"] || "",
          ).toLowerCase();
          const status = String(row.Status || "").toLowerCase();
          const paymentStatus = String(
            row["Payment Status"] || "",
          ).toLowerCase();

          // only select draft invoices that are valid for posting and not paid
          return (
            validForPosting === "yes" &&
            status === "draft" &&
            paymentStatus !== "paid"
          );
        })
        .map(getRowId);

      onUpdateSelection(new Set(eligibleRowKeys));
    }
  }, [autoSelectEligible, reportData, onUpdateSelection]);

  // analyze selected rows for bulk ID composition
  const selectedInvoices = useMemo(() => {
    return Array.from(selectedRows)
      .map((rowId) => {
        const row = reportData.find((r) => getRowId(r) === rowId);
        if (!row) return null;
        return {
          id: Number(row["KAboodle Invoice ID"]),
          bulkId: String(row["Bulk ID"] || ""),
          status: String(row.Status || "").toLowerCase(),
          validForPosting: String(row["Valid for Posting"] || "").toLowerCase(),
        };
      })
      .filter(
        (
          inv,
        ): inv is {
          id: number;
          bulkId: string;
          status: string;
          validForPosting: string;
        } => inv !== null && !isNaN(inv.id),
      );
  }, [selectedRows, reportData]);

  // invoices eligible for batch posting (draft only)
  const postableInvoices = useMemo(
    () => selectedInvoices.filter((inv) => inv.status === "draft"),
    [selectedInvoices],
  );

  const bulkAnalysis = useMemo(() => {
    const bulkIds = new Set(
      selectedInvoices.map((inv) => inv.bulkId).filter((id) => id !== ""),
    );
    const hasNonBulk = selectedInvoices.some((inv) => inv.bulkId === "");
    const hasBulk = bulkIds.size > 0;

    return {
      bulkIds,
      hasNonBulk,
      hasBulk,
      isMixed: hasBulk && hasNonBulk,
      hasMultipleBulkGroups: bulkIds.size > 1,
      singleBulkId: bulkIds.size === 1 ? Array.from(bulkIds)[0] : null,
    };
  }, [selectedInvoices]);

  // compute a summary of what will be posted
  const postSummary = useMemo(() => {
    const selectedRowData = Array.from(selectedRows)
      .map((rowId) => reportData.find((r) => getRowId(r) === rowId))
      .filter(Boolean) as Record<string, unknown>[];

    const totalAmount = selectedRowData.reduce(
      (sum, row) => sum + Number(row["Total Amount"] || 0),
      0,
    );
    const clientNames = [
      ...new Set(
        selectedRowData
          .map((row) => String(row["Client Name"] || ""))
          .filter(Boolean),
      ),
    ];
    const serviceDates = selectedRowData
      .flatMap((row) => {
        const from = String(row["Service Date From"] || "");
        const to = String(row["Service Date To"] || "");
        return [from, to].filter(Boolean);
      })
      .sort();

    // count unique claims from Claim ID column (comma-separated per row)
    const claimIds = new Set<string>();
    selectedRowData.forEach((row) => {
      const ids = String(row["Claim ID"] || "");
      if (ids) ids.split(",").forEach((id) => claimIds.add(id.trim()));
    });

    return {
      invoiceCount: selectedRowData.length,
      totalAmount,
      clientName: clientNames.length === 1 ? clientNames[0] : null,
      earliestDate: serviceDates[0] || null,
      latestDate: serviceDates[serviceDates.length - 1] || null,
      claimCount: claimIds.size,
    };
  }, [selectedRows, reportData]);

  // server-fetched summary for the entire bulk group
  const [fullBatchSummary, setFullBatchSummary] = useState<{
    invoiceCount: number;
    totalAmount: number;
    clientName: string | null;
    earliestDate: string | null;
    latestDate: string | null;
    claimCount: number;
  } | null>(null);

  const [showPostConfirmation, setShowPostConfirmation] = useState<
    "non_bulk" | null
  >(null);
  const [showEntireBatchConfirmation, setShowEntireBatchConfirmation] =
    useState(false);

  // find the single selected row that has a delete URL
  const deletableInvoice = useMemo(() => {
    const rows = Array.from(selectedRows);
    if (rows.length !== 1) return null;
    const row = reportData.find((r) => getRowId(r) === rows[0]);
    if (!row || !row["Delete URL"]) return null;
    return {
      id: Number(row["KAboodle Invoice ID"]),
      bulkId: String(row["Bulk ID"] || ""),
    };
  }, [selectedRows, reportData]);

  const handleDeleteClick = () => {
    if (!deletableInvoice) return;
    if (deletableInvoice.bulkId) {
      setShowDeleteConfirmation("batch_prompt");
    } else {
      setShowDeleteConfirmation("single");
    }
  };

  const handleDeleteInvoice = async (deleteBatch: boolean) => {
    setShowDeleteConfirmation(null);
    if (!deletableInvoice) return;

    try {
      await deleteInvoiceById({
        id: deletableInvoice.id,
        delete_batch: deleteBatch || undefined,
      }).unwrap();

      KaboodleToaster.success(
        deleteBatch
          ? "Successfully deleted the entire batch. Rerun report to see updates."
          : "Successfully deleted the invoice. Rerun report to see updates.",
      );
      onClearSelection();
    } catch (error: unknown) {
      const errorMessage =
        (error as { data?: { error?: string } })?.data?.error ||
        "Failed to delete invoice";
      KaboodleToaster.error(errorMessage);
    }
  };

  // --- Void logic ---
  const voidableInvoices = useMemo(() => {
    return Array.from(selectedRows)
      .map((rowId) => {
        const row = reportData.find((r) => getRowId(r) === rowId);
        if (!row) return null;
        const invoiceStatus = String(row.Status || "").toLowerCase();
        if (invoiceStatus !== "finalized") return null;
        return {
          id: Number(row["KAboodle Invoice ID"]),
          bulkId: String(row["Bulk ID"] || ""),
        };
      })
      .filter(Boolean) as { id: number; bulkId: string }[];
  }, [selectedRows, reportData]);

  const handleVoidClick = () => {
    if (voidableInvoices.length === 0) return;
    const hasBulk = voidableInvoices.some((inv) => inv.bulkId);
    if (hasBulk) {
      setShowVoidConfirmation("bulk_prompt");
    } else {
      setShowVoidConfirmation("single");
    }
  };

  const handleVoidSelected = async () => {
    setShowVoidConfirmation(null);
    try {
      const ids = voidableInvoices.map((inv) => inv.id);
      await batchVoidInvoices({ invoice_ids: ids }).unwrap();
      KaboodleToaster.success(
        ids.length === 1
          ? "Invoice voided successfully. Rerun report to see updates."
          : "Voiding invoices in background. Check Background Jobs for progress.",
      );
      onClearSelection();
    } catch (error: unknown) {
      const errorMessage =
        (error as { data?: { error?: string } })?.data?.error ||
        "Failed to void invoice(s)";
      KaboodleToaster.error(errorMessage);
    }
  };

  const handleVoidEntireBulk = async () => {
    setShowVoidConfirmation(null);
    const bulkIds = new Set(
      voidableInvoices.map((inv) => inv.bulkId).filter(Boolean),
    );
    if (bulkIds.size !== 1) return;
    const bulkId = Array.from(bulkIds)[0];

    try {
      const result = await fetchBatchVoidSummary(bulkId).unwrap();
      setVoidBatchSummary({
        invoiceCount: result.invoice_count,
        totalAmount: result.total_amount,
        clientName: result.client_name || null,
        earliestDate: result.earliest_service_date,
        latestDate: result.latest_service_date,
      });
      setShowVoidConfirmation("bulk_confirm");
    } catch {
      KaboodleToaster.error("Failed to load batch summary for voiding");
    }
  };

  const handleConfirmVoidEntireBulk = async () => {
    setShowVoidConfirmation(null);
    try {
      const ids = voidableInvoices.map((inv) => inv.id);
      await batchVoidInvoices({
        invoice_ids: ids,
        void_entire_bulk: true,
      }).unwrap();
      KaboodleToaster.success(
        "Voiding entire batch in background. Check Background Jobs for progress.",
      );
      onClearSelection();
    } catch (error: unknown) {
      const errorMessage =
        (error as { data?: { error?: string } })?.data?.error ||
        "Failed to void invoices";
      KaboodleToaster.error(errorMessage);
    }
  };

  const postInvoices = async (invoiceIds: number[], postEntireBulk = false) => {
    try {
      await batchPostInvoices({
        invoice_ids: invoiceIds,
        post_entire_bulk: postEntireBulk || undefined,
      }).unwrap();
      KaboodleToaster.success(
        `Batch posting invoices in background. Check Background Jobs for progress, then rerun report to see updates.`,
      );
      onClearSelection();
    } catch (error: unknown) {
      const errorMessage =
        (error as { data?: { error?: string } })?.data?.error ||
        "Failed to batch post invoices";
      KaboodleToaster.error(errorMessage);
    }
  };

  const handlePostSelectedOnly = async () => {
    setShowBulkConfirmation(false);
    const ids = postableInvoices.map((inv) => inv.id);
    await postInvoices(ids);
  };

  const handlePostEntireBulk = async () => {
    setShowBulkConfirmation(false);
    if (!bulkAnalysis.singleBulkId) return;

    try {
      const result = await fetchBatchSummary(
        bulkAnalysis.singleBulkId,
      ).unwrap();
      setFullBatchSummary({
        invoiceCount: result.invoice_count,
        totalAmount: result.total_amount,
        clientName: result.client_name || null,
        earliestDate: result.earliest_service_date,
        latestDate: result.latest_service_date,
        claimCount: result.claim_count,
      });
      setShowEntireBatchConfirmation(true);
    } catch {
      KaboodleToaster.error("Failed to load batch summary");
    }
  };

  const handleConfirmEntireBulkPost = async () => {
    setShowEntireBatchConfirmation(false);
    const ids = postableInvoices.map((inv) => inv.id);
    await postInvoices(ids, true);
  };

  const handleBatchPostInvoices = async () => {
    if (postableInvoices.length === 0) {
      KaboodleToaster.error("No draft invoices selected for posting");
      return;
    }

    // Validate bulk ID composition
    if (bulkAnalysis.isMixed) {
      KaboodleToaster.error(
        "Cannot mix bulk (Section 111) and non-bulk invoices in the same batch post. Please select only one type.",
      );
      return;
    }

    if (bulkAnalysis.hasMultipleBulkGroups) {
      KaboodleToaster.error(
        "Cannot batch post invoices from different bulk groups. Please select invoices from a single batch only.",
      );
      return;
    }

    // If bulk invoices selected, show confirmation
    if (bulkAnalysis.hasBulk && bulkAnalysis.singleBulkId) {
      setShowBulkConfirmation(true);
      return;
    }

    // Non-bulk invoices — show confirmation
    setShowPostConfirmation("non_bulk");
  };

  const handleConfirmNonBulkPost = async () => {
    setShowPostConfirmation(null);
    const ids = postableInvoices.map((inv) => inv.id);
    await postInvoices(ids);
  };

  return (
    <>
      {isConnectionLoading ? (
        <Button className="report-button" onClick={() => {}} disabled>
          <span className="material-icons">post_add</span>
          Connecting...
        </Button>
      ) : !isConnectionValid ? (
        <Tooltip
          content={
            connectionMessage || "Connect to QuickBooks to batch post invoices"
          }
          variant="info"
          className="batch-invoice-tooltip-container"
        >
          <Button className="report-button" onClick={startReconnection}>
            <span className="material-icons">refresh</span>
            Reconnect QuickBooks
          </Button>
        </Tooltip>
      ) : postableInvoices.length > 0 ? (
        <Tooltip
          content={`Batch post ${postableInvoices.length} draft invoice${postableInvoices.length === 1 ? "" : "s"}`}
          variant="info"
          className="batch-invoice-tooltip-container"
        >
          <Button
            type="button"
            onClick={handleBatchPostInvoices}
            disabled={isBatchPosting}
            className="report-button success"
          >
            <span className="material-icons">post_add</span>
            {isBatchPosting
              ? "Batch Posting..."
              : `Batch Post Invoices (${postableInvoices.length})`}
          </Button>
        </Tooltip>
      ) : null}

      {/* Void button — visible when at least one selected invoice is Finalized */}
      {voidableInvoices.length > 0 && (
        <Tooltip
          content={`Void ${voidableInvoices.length} finalized invoice${voidableInvoices.length === 1 ? "" : "s"}`}
          variant="info"
          className="batch-invoice-tooltip-container"
        >
          <Button
            type="button"
            onClick={handleVoidClick}
            disabled={isVoiding}
            className="report-button danger"
          >
            <span className="material-icons">block</span>
            {isVoiding
              ? "Voiding..."
              : `Void Invoice${voidableInvoices.length === 1 ? "" : "s"} (${voidableInvoices.length})`}
          </Button>
        </Tooltip>
      )}

      {/* Delete button — only visible when exactly one selected row has a delete URL */}
      {deletableInvoice && (
        <Tooltip
          content="Delete the selected invoice"
          variant="info"
          className="batch-invoice-tooltip-container"
        >
          <Button
            type="button"
            onClick={handleDeleteClick}
            disabled={isDeleting}
            className="report-button danger"
          >
            <span className="material-icons">delete</span>
            {isDeleting ? "Deleting..." : "Delete Invoice"}
          </Button>
        </Tooltip>
      )}

      {/* Delete confirmation — single invoice (no batch) */}
      {showDeleteConfirmation === "single" && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
          onClick={() => setShowDeleteConfirmation(null)}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "8px",
              padding: "24px",
              maxWidth: "500px",
              width: "90%",
              boxShadow: "0 4px 24px rgba(0,0,0,0.15)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: "0 0 16px", fontSize: "16px" }}>
              Confirm Delete
            </h3>
            <p
              style={{
                fontSize: "14px",
                lineHeight: "1.6",
                color: "#333",
                marginBottom: "20px",
              }}
            >
              Are you sure you want to delete this invoice? This action cannot
              be undone.
            </p>
            <div
              style={{
                display: "flex",
                gap: "8px",
                justifyContent: "flex-end",
              }}
            >
              <Button onClick={() => setShowDeleteConfirmation(null)}>
                Cancel
              </Button>
              <Button
                className="danger"
                onClick={() => handleDeleteInvoice(false)}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation — invoice belongs to a batch */}
      {showDeleteConfirmation === "batch_prompt" && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
          onClick={() => setShowDeleteConfirmation(null)}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "8px",
              padding: "24px",
              maxWidth: "500px",
              width: "90%",
              boxShadow: "0 4px 24px rgba(0,0,0,0.15)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: "0 0 16px", fontSize: "16px" }}>
              Batch Invoice Detected
            </h3>
            <div
              style={{
                fontSize: "14px",
                lineHeight: "1.6",
                color: "#333",
                marginBottom: "20px",
              }}
            >
              <p style={{ margin: "0 0 8px" }}>
                This invoice is part of a Section 111 invoice batch.
              </p>
              <p style={{ margin: "0" }}>
                You can delete just this invoice, or delete all invoices in the
                entire batch. This action cannot be undone.
              </p>
            </div>
            <div
              style={{
                display: "flex",
                gap: "8px",
                justifyContent: "flex-end",
                flexWrap: "wrap",
              }}
            >
              <Button onClick={() => setShowDeleteConfirmation(null)}>
                Cancel
              </Button>
              <Button
                className="danger"
                onClick={() => handleDeleteInvoice(false)}
              >
                Delete This Invoice
              </Button>
              <Button
                className="danger"
                onClick={() => handleDeleteInvoice(true)}
              >
                Delete Entire Batch
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Non-bulk post confirmation dialog */}
      {showPostConfirmation === "non_bulk" && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
          onClick={() => setShowPostConfirmation(null)}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "8px",
              padding: "24px",
              maxWidth: "500px",
              width: "90%",
              boxShadow: "0 4px 24px rgba(0,0,0,0.15)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: "0 0 16px", fontSize: "16px" }}>
              Confirm Batch Post
            </h3>
            <div
              style={{
                fontSize: "14px",
                lineHeight: "1.6",
                color: "#333",
                marginBottom: "20px",
              }}
            >
              <p style={{ margin: "0 0 8px" }}>
                You are about to post{" "}
                <strong>
                  {postSummary.invoiceCount} invoice
                  {postSummary.invoiceCount === 1 ? "" : "s"}
                </strong>{" "}
                totalling{" "}
                <strong>
                  $
                  {postSummary.totalAmount.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </strong>
                .
              </p>
            </div>
            <div
              style={{
                display: "flex",
                gap: "8px",
                justifyContent: "flex-end",
              }}
            >
              <Button onClick={() => setShowPostConfirmation(null)}>
                Cancel
              </Button>
              <Button className="success" onClick={handleConfirmNonBulkPost}>
                Post Invoices
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk invoice confirmation dialog */}
      {showBulkConfirmation && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
          onClick={() => setShowBulkConfirmation(false)}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "8px",
              padding: "24px",
              maxWidth: "520px",
              width: "90%",
              boxShadow: "0 4px 24px rgba(0,0,0,0.15)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: "0 0 16px", fontSize: "16px" }}>
              Confirm Batch Post
            </h3>
            <div
              style={{
                fontSize: "14px",
                lineHeight: "1.6",
                color: "#333",
                marginBottom: "20px",
              }}
            >
              <p style={{ margin: "0 0 8px" }}>
                You are about to post{" "}
                <strong>
                  {postSummary.invoiceCount} invoice
                  {postSummary.invoiceCount === 1 ? "" : "s"}
                </strong>{" "}
                totalling{" "}
                <strong>
                  $
                  {postSummary.totalAmount.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </strong>
                .
              </p>
              <table
                style={{
                  width: "100%",
                  fontSize: "13px",
                  borderCollapse: "collapse",
                  marginTop: "12px",
                }}
              >
                <tbody>
                  {postSummary.clientName && (
                    <tr>
                      <td
                        style={{
                          padding: "4px 8px 4px 0",
                          color: "#666",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Client
                      </td>
                      <td style={{ padding: "4px 0", fontWeight: 500 }}>
                        {postSummary.clientName}
                      </td>
                    </tr>
                  )}
                  {postSummary.claimCount > 0 && (
                    <tr>
                      <td
                        style={{
                          padding: "4px 8px 4px 0",
                          color: "#666",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Claims
                      </td>
                      <td style={{ padding: "4px 0", fontWeight: 500 }}>
                        {postSummary.claimCount}
                      </td>
                    </tr>
                  )}
                  {postSummary.earliestDate && postSummary.latestDate && (
                    <tr>
                      <td
                        style={{
                          padding: "4px 8px 4px 0",
                          color: "#666",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Service Dates
                      </td>
                      <td style={{ padding: "4px 0", fontWeight: 500 }}>
                        {postSummary.earliestDate === postSummary.latestDate
                          ? postSummary.earliestDate
                          : `${postSummary.earliestDate} — ${postSummary.latestDate}`}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              <p style={{ margin: "12px 0 0", color: "#666" }}>
                You can post just your selection, or post all draft invoices in
                the entire batch via the QuickBooks Batch API.
              </p>
            </div>
            <div
              style={{
                display: "flex",
                gap: "8px",
                justifyContent: "flex-end",
                flexWrap: "wrap",
              }}
            >
              <Button onClick={() => setShowBulkConfirmation(false)}>
                Cancel
              </Button>
              <Button onClick={handlePostSelectedOnly}>
                Post Selected Only ({selectedInvoices.length})
              </Button>
              <Button className="success" onClick={handlePostEntireBulk}>
                Post Entire Batch
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Entire batch confirmation — shows full batch summary */}
      {showEntireBatchConfirmation && fullBatchSummary && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
          onClick={() => setShowEntireBatchConfirmation(false)}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "8px",
              padding: "24px",
              maxWidth: "520px",
              width: "90%",
              boxShadow: "0 4px 24px rgba(0,0,0,0.15)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: "0 0 16px", fontSize: "16px" }}>
              Post Entire Batch
            </h3>
            <div
              style={{
                fontSize: "14px",
                lineHeight: "1.6",
                color: "#333",
                marginBottom: "20px",
              }}
            >
              <p style={{ margin: "0 0 8px" }}>
                You are about to post all{" "}
                <strong>
                  {fullBatchSummary.invoiceCount} draft invoice
                  {fullBatchSummary.invoiceCount === 1 ? "" : "s"}
                </strong>{" "}
                in this batch totalling{" "}
                <strong>
                  $
                  {fullBatchSummary.totalAmount.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </strong>
                .
              </p>
              <table
                style={{
                  width: "100%",
                  fontSize: "13px",
                  borderCollapse: "collapse",
                  marginTop: "12px",
                }}
              >
                <tbody>
                  {fullBatchSummary.clientName && (
                    <tr>
                      <td
                        style={{
                          padding: "4px 8px 4px 0",
                          color: "#666",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Client
                      </td>
                      <td style={{ padding: "4px 0", fontWeight: 500 }}>
                        {fullBatchSummary.clientName}
                      </td>
                    </tr>
                  )}
                  {fullBatchSummary.claimCount > 0 && (
                    <tr>
                      <td
                        style={{
                          padding: "4px 8px 4px 0",
                          color: "#666",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Claims
                      </td>
                      <td style={{ padding: "4px 0", fontWeight: 500 }}>
                        {fullBatchSummary.claimCount}
                      </td>
                    </tr>
                  )}
                  {fullBatchSummary.earliestDate &&
                    fullBatchSummary.latestDate && (
                      <tr>
                        <td
                          style={{
                            padding: "4px 8px 4px 0",
                            color: "#666",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Service Dates
                        </td>
                        <td style={{ padding: "4px 0", fontWeight: 500 }}>
                          {fullBatchSummary.earliestDate ===
                          fullBatchSummary.latestDate
                            ? fullBatchSummary.earliestDate
                            : `${fullBatchSummary.earliestDate} — ${fullBatchSummary.latestDate}`}
                        </td>
                      </tr>
                    )}
                </tbody>
              </table>
            </div>
            <div
              style={{
                display: "flex",
                gap: "8px",
                justifyContent: "flex-end",
              }}
            >
              <Button onClick={() => setShowEntireBatchConfirmation(false)}>
                Cancel
              </Button>
              <Button className="success" onClick={handleConfirmEntireBulkPost}>
                Post Entire Batch ({fullBatchSummary.invoiceCount})
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Void confirmation — single or non-bulk */}
      {showVoidConfirmation === "single" && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
          onClick={() => setShowVoidConfirmation(null)}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "8px",
              padding: "24px",
              maxWidth: "500px",
              width: "90%",
              boxShadow: "0 4px 24px rgba(0,0,0,0.15)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: "0 0 16px", fontSize: "16px" }}>
              Confirm Void
            </h3>
            <p
              style={{
                fontSize: "14px",
                lineHeight: "1.6",
                color: "#333",
                marginBottom: "20px",
              }}
            >
              You are about to void{" "}
              <strong>
                {voidableInvoices.length} invoice
                {voidableInvoices.length === 1 ? "" : "s"}
              </strong>
              . This action cannot be undone.
            </p>
            <div
              style={{
                display: "flex",
                gap: "8px",
                justifyContent: "flex-end",
              }}
            >
              <Button onClick={() => setShowVoidConfirmation(null)}>
                Cancel
              </Button>
              <Button className="danger" onClick={handleVoidSelected}>
                Void Invoice{voidableInvoices.length === 1 ? "" : "s"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Void confirmation — bulk prompt (void selected vs entire batch) */}
      {showVoidConfirmation === "bulk_prompt" && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
          onClick={() => setShowVoidConfirmation(null)}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "8px",
              padding: "24px",
              maxWidth: "520px",
              width: "90%",
              boxShadow: "0 4px 24px rgba(0,0,0,0.15)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: "0 0 16px", fontSize: "16px" }}>
              Void Bulk Invoices
            </h3>
            <div
              style={{
                fontSize: "14px",
                lineHeight: "1.6",
                color: "#333",
                marginBottom: "20px",
              }}
            >
              <p style={{ margin: "0 0 8px" }}>
                The selected invoices are part of a Section 111 batch. You can
                void just your selection or void all finalized invoices in the
                batch.
              </p>
            </div>
            <div
              style={{
                display: "flex",
                gap: "8px",
                justifyContent: "flex-end",
                flexWrap: "wrap",
              }}
            >
              <Button onClick={() => setShowVoidConfirmation(null)}>
                Cancel
              </Button>
              <Button className="danger" onClick={handleVoidSelected}>
                Void Selected ({voidableInvoices.length})
              </Button>
              <Button className="danger" onClick={handleVoidEntireBulk}>
                Void Entire Batch
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Void confirmation — entire batch details */}
      {showVoidConfirmation === "bulk_confirm" && voidBatchSummary && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
          onClick={() => setShowVoidConfirmation(null)}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "8px",
              padding: "24px",
              maxWidth: "520px",
              width: "90%",
              boxShadow: "0 4px 24px rgba(0,0,0,0.15)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: "0 0 16px", fontSize: "16px" }}>
              Void Entire Batch
            </h3>
            <div
              style={{
                fontSize: "14px",
                lineHeight: "1.6",
                color: "#333",
                marginBottom: "20px",
              }}
            >
              <p style={{ margin: "0 0 8px" }}>
                You are about to void all{" "}
                <strong>
                  {voidBatchSummary.invoiceCount} finalized invoice
                  {voidBatchSummary.invoiceCount === 1 ? "" : "s"}
                </strong>{" "}
                totalling{" "}
                <strong>
                  $
                  {voidBatchSummary.totalAmount.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </strong>
                . This action cannot be undone.
              </p>
              <table
                style={{
                  width: "100%",
                  fontSize: "13px",
                  borderCollapse: "collapse",
                  marginTop: "12px",
                }}
              >
                <tbody>
                  {voidBatchSummary.clientName && (
                    <tr>
                      <td
                        style={{
                          padding: "4px 8px 4px 0",
                          color: "#666",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Client
                      </td>
                      <td style={{ padding: "4px 0", fontWeight: 500 }}>
                        {voidBatchSummary.clientName}
                      </td>
                    </tr>
                  )}
                  {voidBatchSummary.earliestDate &&
                    voidBatchSummary.latestDate && (
                      <tr>
                        <td
                          style={{
                            padding: "4px 8px 4px 0",
                            color: "#666",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Service Dates
                        </td>
                        <td style={{ padding: "4px 0", fontWeight: 500 }}>
                          {voidBatchSummary.earliestDate ===
                          voidBatchSummary.latestDate
                            ? voidBatchSummary.earliestDate
                            : `${voidBatchSummary.earliestDate} — ${voidBatchSummary.latestDate}`}
                        </td>
                      </tr>
                    )}
                </tbody>
              </table>
            </div>
            <div
              style={{
                display: "flex",
                gap: "8px",
                justifyContent: "flex-end",
              }}
            >
              <Button onClick={() => setShowVoidConfirmation(null)}>
                Cancel
              </Button>
              <Button className="danger" onClick={handleConfirmVoidEntireBulk}>
                Void Entire Batch ({voidBatchSummary.invoiceCount})
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BatchInvoiceActions;
